import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { Overlay } from "config/enums/overlays";
import { UpdateUserMultiError, useAuth } from "hooks/useAuth";
import { OptionsActions, updateSaveableOptions } from "./actions";
import {
  initialOptionsState,
  NonSaveableOptions,
  pickSaveableOptions,
  SAVEABLE_OPTION_KEYS,
  SaveableOptions,
} from "./state";

type OptionsContextType = {
  loading: boolean;
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
  state: SaveableOptions & NonSaveableOptions;
  dispatch: React.Dispatch<OptionsActions>;
  toggleOverlay: (overlay: Overlay) => void;
  updateSaveable: (
    options: Partial<SaveableOptions>,
    saveToServer?: boolean
  ) => void;
};

export const OptionsContext = React.createContext<OptionsContextType>({
  loading: false,
  setLoading: () => null,
  state: initialOptionsState,
  dispatch: () => undefined,
  toggleOverlay: () => null,
  updateSaveable: () => null,
});

export const useOptions = () => useContext(OptionsContext);

/**
 * All saveable options: the changed ones, the rest from the current state.
 * The server stores the settings as one object, so they're always sent all together.
 */
export const mergeOptions = (
  options: Partial<SaveableOptions>,
  state: SaveableOptions
): Partial<SaveableOptions> => ({
  ...pickSaveableOptions(state),
  ...pickSaveableOptions(options),
});

export const OptionsProvider = (props: {
  children: React.ReactNode;
  reducerTuple: [
    SaveableOptions & NonSaveableOptions,
    React.Dispatch<OptionsActions>,
  ];
}) => {
  const { children, reducerTuple } = props;
  const [state, dispatch] = reducerTuple;
  const { update, user } = useAuth();
  // Settings of a user signed in from a previous visit (stored locally), applied right away,
  // before the current ones are loaded from the server (see App)
  const [initialUserSettings] = useState(user?.settings);
  const [loading, setLoading] = useState(false);
  // The state including updates that weren't rendered yet, so two updates in a row
  // (e.g. the language and the other settings on sign in) don't overwrite each other
  const latestStateRef = useRef(state);
  useEffect(() => {
    latestStateRef.current = state;
  }, [state]);

  const updateSaveable = useCallback(
    (options: Partial<SaveableOptions>, saveToServer: boolean = true) => {
      const revertState = latestStateRef.current;
      const changedOptions = pickSaveableOptions(options);
      latestStateRef.current = { ...revertState, ...changedOptions };

      dispatch(updateSaveableOptions(changedOptions));

      if (saveToServer && user) {
        update(mergeOptions(changedOptions, revertState)).catch((error) => {
          if (error instanceof UpdateUserMultiError) {
            const revertedOptions = error.errorCodes.reduce(
              (acc, errorCode) => {
                if (
                  errorCode === "newsletter_subscribe_error" ||
                  errorCode === "newsletter_unsubscribe_error"
                ) {
                  return {
                    ...acc,
                    receiveNewsletter: revertState.receiveNewsletter,
                  };
                }
                return acc;
              },
              {} as Partial<SaveableOptions>
            );

            dispatch(updateSaveableOptions(revertedOptions));
          } else {
            // This is more error prone as the solution above, since in theory we don't know
            // which one of the options triggered the error on the backend - it is very backend
            // dependend, so is the solution above with custom errors, we have to mirror the
            // backend and it's not generic, but at least there we know what we need to revert
            const revertedOptions: Partial<SaveableOptions> = {};
            const revertOption = <Key extends keyof SaveableOptions>(
              key: Key
            ) => {
              revertedOptions[key] = revertState[key];
            };
            SAVEABLE_OPTION_KEYS.filter((key) => key in changedOptions).forEach(
              revertOption
            );

            dispatch(updateSaveableOptions(revertedOptions));
          }
        });
      }
    },
    [dispatch, update, user]
  );

  useEffect(() => {
    if (!initialUserSettings) return;
    dispatch(updateSaveableOptions(pickSaveableOptions(initialUserSettings)));
  }, [dispatch, initialUserSettings]);

  const toggleOverlay = useCallback(
    (clickedOverlay: Overlay) => {
      const { mapOverlays } = latestStateRef.current;

      updateSaveable({
        mapOverlays: mapOverlays.includes(clickedOverlay)
          ? mapOverlays.filter((overlay) => overlay !== clickedOverlay)
          : [...mapOverlays, clickedOverlay],
      });
    },
    [updateSaveable]
  );

  return (
    <OptionsContext.Provider
      value={{
        loading,
        setLoading,
        state,
        dispatch,
        updateSaveable,
        toggleOverlay,
      }}
    >
      {children}
    </OptionsContext.Provider>
  );
};
